import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, ArrowLeft, Plus, Search, Edit2, Trash2,
  Users, Mail, Calendar, AlertCircle, Shield, ShieldOff, RefreshCw
} from '../../components/icons';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface Partner {
  id: string;
  name: string;
  email: string;
  userId: string;
  coachLimit: number;
  isActive: boolean;
  createdAt: Date;
}

export function PartnerManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [togglingStatusIds, setTogglingStatusIds] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    coachLimit: 10,
    locale: 'en' as 'en' | 'tr'
  });

  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const { data: userData } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        if (!userData || userData.role !== 'admin') {
          navigate('/login');
          return;
        }

        await loadPartners();
      } catch (err) {
        console.error('Error checking admin status:', err);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [user, navigate]);

  const loadPartners = async () => {
    try {
      const { data: partnersData, error: partnersError } = await supabase
        .from('partners')
        .select('id, user_id, max_coaches, is_active, created_at, users!inner(email, full_name)');

      if (partnersError) throw partnersError;

      const formattedPartners = partnersData?.map(p => {
        const userData = p.users as any;
        return {
          id: p.id,
          name: userData?.full_name || '',
          email: userData?.email || '',
          userId: p.user_id,
          coachLimit: p.max_coaches || 10,
          isActive: p.is_active !== false,
          createdAt: p.created_at ? new Date(p.created_at) : new Date()
        };
      }) || [];

      setPartners(formattedPartners);
    } catch (err) {
      console.error('Error loading partners:', err);
      setError('Failed to load partners');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      let createResult: any = null;
      if (editingPartner) {
        await supabase
          .from('partners')
          .update({ max_coaches: formData.coachLimit })
          .eq('id', editingPartner.id);

        await supabase
          .from('users')
          .update({ full_name: formData.name, email: formData.email })
          .eq('id', editingPartner.userId);
      } else {
        const session = await supabase.auth.getSession();
        const token = session.data.session?.access_token;
        if (!token) throw new Error('Not authenticated');

        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-partner`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            email: formData.email,
            name: formData.name,
            password: formData.password,
            coachLimit: formData.coachLimit,
            locale: formData.locale,
          }),
        });

        createResult = await response.json();
        if (!response.ok) {
          throw new Error(createResult.error || 'Failed to create partner account');
        }
      }

      const wasAdding = !editingPartner;
      await loadPartners();
      setShowAddModal(false);
      setEditingPartner(null);
      setFormData({ name: '', email: '', password: '', coachLimit: 10, locale: 'en' });

      if (wasAdding && createResult) {
        const emailInfo = createResult.emailSent
          ? 'Login details have been emailed to the partner.'
          : `Partner created but email failed: ${createResult.emailError || 'Unknown error'}`;
        alert(`Partner account created successfully! ${emailInfo}`);
      }
    } catch (err: any) {
      console.error('Error saving partner:', err);
      setError(err.message || 'Failed to save partner');
    }
  };

  const handleDelete = async (partnerId: string) => {
    if (!confirm('Are you sure you want to delete this partner?')) return;

    try {
      await supabase.from('partners').delete().eq('id', partnerId);
      await loadPartners();
    } catch (err) {
      console.error('Error deleting partner:', err);
      setError('Failed to delete partner');
    }
  };

  const resendCredentials = async (partner: Partner) => {
    if (!confirm(`This will reset ${partner.name}'s password and send them a new login email. Continue?`)) return;
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'resend-credentials-partner',
            recipientEmail: partner.email,
            recipientName: partner.name,
            userId: partner.userId,
          }),
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to send email');
      setSuccessMessage(`Login details resent to ${partner.email}`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to resend credentials');
    }
  };

  const openEditModal = (partner: Partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      email: partner.email,
      password: '',
      coachLimit: partner.coachLimit,
      locale: 'en'
    });
    setShowAddModal(true);
  };

  const handleToggleActive = async (partner: Partner) => {
    setTogglingStatusIds(prev => new Set(prev).add(partner.id));
    try {
      const { error } = await supabase
        .from('partners')
        .update({
          is_active: !partner.isActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', partner.id);

      if (error) throw error;

      setSuccessMessage(
        partner.isActive
          ? `Access disabled for ${partner.name}`
          : `Access enabled for ${partner.name}`
      );
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadPartners();
    } catch (err) {
      console.error('Error toggling partner access:', err);
      setError('Failed to update partner access');
    } finally {
      setTogglingStatusIds(prev => {
        const next = new Set(prev);
        next.delete(partner.id);
        return next;
      });
    }
  };

  const filteredPartners = partners.filter(partner => {
    if (statusFilter === 'active' && !partner.isActive) return false;
    if (statusFilter === 'disabled' && partner.isActive) return false;
    return partner.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      partner.email.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (isLoading) {
    return <LoadingSpinner message="Loading partner management..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link
            to="/admin"
            className="flex items-center gap-2 text-chocolate-600 hover:text-sage-600 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-sage-500 rounded-xl shadow-soft">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <h1 className="freud-heading-xl text-chocolate-900">Partner Management</h1>
            </div>
            <button
              onClick={() => {
                setEditingPartner(null);
                setFormData({ name: '', email: '', password: '', coachLimit: 10, locale: 'en' });
                setShowAddModal(true);
              }}
              className="freud-btn-primary flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Partner
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}
        {successMessage && (
          <div className="bg-sage-100 border border-sage-200 text-sage-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            {successMessage}
          </div>
        )}

        <div className="freud-card-elevated mb-6">
          <div className="p-4 border-b border-cream-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
              <input
                type="text"
                placeholder="Search partners..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="freud-input pl-10"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="freud-input"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream-100">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Partner</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Email</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Coach Limit</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Status</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Created</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-chocolate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-200">
                {filteredPartners.map((partner) => (
                  <tr key={partner.id} className="hover:bg-cream-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-sage-100 rounded-lg">
                          <Building2 className="w-4 h-4 text-sage-600" />
                        </div>
                        <span className="font-medium text-chocolate-900">{partner.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Mail className="w-4 h-4" />
                        {partner.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Users className="w-4 h-4" />
                        {partner.coachLimit} coaches
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        partner.isActive
                          ? 'bg-success-10 text-success-60'
                          : 'bg-coral-100 text-coral-600'
                      }`}>
                        {partner.isActive ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Calendar className="w-4 h-4" />
                        {partner.createdAt.toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleToggleActive(partner)}
                          disabled={togglingStatusIds.has(partner.id)}
                          className={`p-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                            partner.isActive
                              ? 'bg-coral-100 text-coral-600 hover:bg-coral-200'
                              : 'bg-success-10 text-success-60 hover:bg-success-20'
                          }`}
                          title={partner.isActive ? 'Disable Access' : 'Enable Access'}
                        >
                          {togglingStatusIds.has(partner.id) ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : partner.isActive ? (
                            <ShieldOff className="w-4 h-4" />
                          ) : (
                            <Shield className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => resendCredentials(partner)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Resend login details"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(partner)}
                          className="p-2 text-chocolate-600 hover:bg-cream-200 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(partner.id)}
                          className="p-2 text-coral-600 hover:bg-coral-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredPartners.length === 0 && (
            <div className="text-center py-12 text-chocolate-400">
              No partners found
            </div>
          )}
        </div>
      </main>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="freud-card-elevated p-6 w-full max-w-md m-4"
          >
            <h2 className="freud-heading-lg text-chocolate-900 mb-6">
              {editingPartner ? 'Edit Partner' : 'Add Partner'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="freud-label block text-chocolate-700 mb-1">
                  Partner Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="freud-input"
                />
              </div>
              <div>
                <label className="freud-label block text-chocolate-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="freud-input"
                />
              </div>
              {!editingPartner && (
                <div>
                  <label className="freud-label block text-chocolate-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingPartner}
                    minLength={8}
                    className="freud-input"
                    placeholder="Minimum 8 characters"
                  />
                </div>
              )}
              <div>
                <label className="freud-label block text-chocolate-700 mb-1">
                  Coach Limit
                </label>
                <input
                  type="number"
                  value={formData.coachLimit}
                  onChange={(e) => setFormData({ ...formData, coachLimit: parseInt(e.target.value) })}
                  min="1"
                  required
                  className="freud-input"
                />
              </div>
              {!editingPartner && (
                <div>
                  <label className="freud-label block text-chocolate-700 mb-1">
                    Language / Region
                  </label>
                  <select
                    value={formData.locale}
                    onChange={(e) => setFormData({ ...formData, locale: e.target.value as 'en' | 'tr' })}
                    className="freud-input"
                  >
                    <option value="en">English</option>
                    <option value="tr">Turkish</option>
                  </select>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingPartner(null);
                  }}
                  className="freud-btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="freud-btn-primary flex-1"
                >
                  {editingPartner ? 'Save Changes' : 'Add Partner'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
